import type {
  PollIncludedResponse,
  PollsAPIResponse,
  PollsMetaData,
  TransformedPollsResponse,
  TransformedVotesResponse,
  Vote,
  VotesAPIResponse,
  VotesAPIResponseIncluded,
  VotesMetaData,
  VotesResponse
} from '@library/types/polls';

interface VoteMap {
  [key: string]: Vote;
}
interface PollMap {
  [key: string]: PollIncludedResponse;
}
export const normalizeVotesResponse = (response: {
  data: VotesAPIResponse[];
  included: VotesAPIResponseIncluded;
  meta: VotesMetaData;
}): TransformedVotesResponse => {
  const { included, data, meta } = response;

  const pollMap = {} as PollMap;
  const voteMap = {} as VoteMap;

  included.forEach((item) => {
    if ('relationships' in item) {
      pollMap[item.id] = { ...item.attributes, ...item.relationships };
    } else {
      voteMap[item.id] = { ...item.attributes };
    }
  });

  const votes = data.map((vote) => {
    const optionId = vote.relationships.option.data.id;
    const pollId = vote.relationships.poll.data.id;
    const voteOption = voteMap[optionId];
    const poll = pollMap[pollId];
    const selection = voteOption?.answer ?? '';
    const winner = poll?.winner?.data?.id ? voteMap[poll.winner.data.id] : null;
    const winningOption = winner ? winner?.answer : '';
    const question = poll?.question || '';
    const options = poll?.options.data.map((item) => {
      return { id: item.id, ...voteMap[item.id] };
    });

    return {
      id: vote.id,
      question,
      options,
      selection,
      winner: winningOption
    };
  });

  return {
    totalCount: meta.total_count,
    totalPages: meta.total_pages,
    currentPage: meta.current_page,
    perPage: meta.per_page,
    votes
  };
};

export const normalizePollsResponse = (response: {
  data: PollsAPIResponse[];
  included: VotesResponse[];
  meta: PollsMetaData;
}): TransformedPollsResponse => {
  const { included, data, meta } = response;

  const includedMap = new Map(
    included.map((item) => [item.id, item.attributes])
  );

  const polls = data.map((poll) => {
    const options = (poll.relationships?.options?.data || []).map((option) => {
      const attributes = includedMap.get(option.id);
      return { ...option, ...(attributes && { ...attributes }) };
    });
    const winner = poll?.relationships.winner?.data?.id ?? null;
    return {
      description: poll.attributes.description,
      endAt: poll.attributes.end_at,
      id: poll.id,
      imageUrl: poll.attributes.image_url,
      question: poll.attributes.question,
      startAt: poll.attributes.start_at,
      bannerDescription: poll.attributes.banner_description,
      bannerHeadline: poll.attributes.banner_headline,
      bannerCTA: poll.attributes.banner_cta,
      options,
      winner
    };
  });

  return {
    totalCount: meta.total_count,
    totalPages: meta.total_pages,
    currentPage: meta.current_page,
    perPage: meta.per_page,
    polls
  };
};
